import React, { useEffect, useMemo, useState } from 'react';
import ComponentContainer from '../../components/component-container/ComponentContainer';
import Spinner from '../../components/spinner/spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Loader2Icon } from 'lucide-react';
import notifyHB from '../../config/notifyHB';
import { CaInfo } from '../../entities/ca-info';
import { CertInfo } from '../../entities/cert-info';
import { getCaInfo } from '../../use-cases/get-ca-info';
import { getCertInfo } from '../../use-cases/get-cert-info';
import CertificateResult from './CertificateResult';
import styles from './consult-certificate.module.css';

const ConsultCertificate: React.FC = () => {
	const [cas, setCas] = useState<CaInfo[]>([]);
	const [isLoadingCas, setIsLoadingCas] = useState(true);
	const [caLoadError, setCaLoadError] = useState('');

	const [selectedCaHash, setSelectedCaHash] = useState('');
	const [serial, setSerial] = useState('');

	const [isQuerying, setIsQuerying] = useState(false);
	const [queryError, setQueryError] = useState('');
	const [cert, setCert] = useState<CertInfo | null>(null);

	useEffect(() => {
		let cancelled = false;
		setIsLoadingCas(true);
		getCaInfo()
			.then((list) => {
				if (cancelled) return;
				const usable = list.filter(
					(ca) => ca.active && ca.hash !== null,
				);
				setCas(usable);
				setCaLoadError('');
			})
			.catch((error) => {
				if (cancelled) return;
				setCaLoadError(
					'No se pudo cargar el listado de autoridades certificadoras.',
				);
				notifyHB('consult-certificate', 'getCaInfo', error);
			})
			.finally(() => {
				if (!cancelled) setIsLoadingCas(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const trimmedSerial = serial.trim();
	const canQuery = useMemo(
		() =>
			selectedCaHash !== '' &&
			/^\d+$/.test(trimmedSerial) &&
			!isQuerying,
		[selectedCaHash, trimmedSerial, isQuerying],
	);

	const handleQuery = async () => {
		setQueryError('');
		setCert(null);
		setIsQuerying(true);
		try {
			const result = await getCertInfo(selectedCaHash, trimmedSerial);
			if (result.ok) {
				setCert(result.data);
			} else if (result.code === 'CA_HASH_NOT_FOUND') {
				setQueryError(
					'La autoridad certificadora seleccionada no es válida o ha sido deprecada.',
				);
			} else if (result.code === 'SERIAL_NOT_FOUND_FOR_CA') {
				setQueryError(
					'No se encontró un certificado con ese número de serie para la autoridad seleccionada.',
				);
			} else {
				setQueryError('Hubo un error al consultar el certificado.');
				notifyHB(
					'consult-certificate',
					'getCertInfo',
					new Error('Unknown error querying cert info'),
				);
			}
		} catch (error) {
			setQueryError('Hubo un error al consultar el certificado.');
			notifyHB('consult-certificate', 'getCertInfo', error);
		} finally {
			setIsQuerying(false);
		}
	};

	return (
		<ComponentContainer>
			<h1>Consultar Certificado</h1>
			<p>
				Selecciona la autoridad certificadora e ingresa el número de
				serie del certificado que deseas validar.
			</p>

			{caLoadError && <div className={styles.notice}>{caLoadError}</div>}

			<div className={styles.form}>
				<div className={styles.field}>
					<Label className={styles.label}>
						Autoridad Certificadora
					</Label>
					<Select
						value={selectedCaHash}
						onValueChange={setSelectedCaHash}
						disabled={isLoadingCas || cas.length === 0}
					>
						<SelectTrigger>
							<SelectValue
								placeholder={
									isLoadingCas
										? 'Cargando...'
										: 'Selecciona una CA'
								}
							/>
						</SelectTrigger>
						<SelectContent>
							{cas.map((ca) => (
								<SelectItem key={ca.hash!} value={ca.hash!}>
									{ca.ca_name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className={styles.field}>
					<Label className={styles.label}>Número de serie</Label>
					<Input
						placeholder="Ingresa el número de serie (decimal)"
						value={serial}
						onChange={(event) => setSerial(event.target.value)}
					/>
				</div>
			</div>

			<div className={styles.actions}>
				<Button onClick={handleQuery} disabled={!canQuery}>
					{isQuerying && <Loader2Icon className="animate-spin" />}
					Consultar
				</Button>
			</div>

			{isQuerying && <Spinner />}

			{queryError && !isQuerying && (
				<div className={styles.notice}>{queryError}</div>
			)}

			{cert && !isQuerying && <CertificateResult cert={cert} />}
		</ComponentContainer>
	);
};

export default ConsultCertificate;
